import type { PropsWithChildren } from 'react'
import { FlatList, Image, Animated as RNAnimated, ScrollView, Text, View } from 'react-native'
import { createAnimatedComponent } from './animated-component'
import {
  cancelAnimation,
  clamp,
  interpolate,
  withDelay,
  withRepeat,
  withSequence,
  withSpring,
  withTiming,
} from './animations'
import { Easing } from './easing'

export { useAnimatedReaction } from './use-animated-reaction'
export { useAnimatedRef, useScrollOffset } from './use-scroll-offset'

import { useAnimatedStyle, useDerivedValue, useSharedValue } from './hooks'
import { interpolateColor } from './interpolate-color'
import { Keyframe } from './keyframe'
import {
  BounceIn,
  FadeIn,
  FadeInDown,
  FadeInLeft,
  FadeInRight,
  FadeInUp,
  FadeOut,
  FadeOutDown,
  FadeOutLeft,
  FadeOutRight,
  FadeOutUp,
  FlipInEasyY,
  FlipOutEasyY,
  LinearTransition,
  ZoomIn,
  ZoomOut,
} from './layout-animations'

export type { AnimationConfig, SharedValue, WebSharedValue } from './types'

export const Extrapolation = {
  CLAMP: 'clamp',
  EXTEND: 'extend',
  IDENTITY: 'identity',
} as const

// @deprecated use Extrapolation
export const Extrapolate = Extrapolation

export function runOnJS<T extends (...args: unknown[]) => unknown>(fn: T): T {
  return fn
}

export function useAnimatedScrollHandler(
  handlers: Record<string, (event: { contentOffset?: { x?: number; y?: number } }) => void>,
) {
  return (event: {
    nativeEvent?: { contentOffset?: { x?: number; y?: number } }
    contentOffset?: { x?: number; y?: number }
  }) => {
    const contentOffset = event.nativeEvent?.contentOffset ?? event.contentOffset

    for (const handler of Object.values(handlers)) {
      handler({ contentOffset })
    }
  }
}

export {
  BounceIn,
  cancelAnimation,
  clamp,
  Easing,
  FadeIn,
  FadeInDown,
  FadeInLeft,
  FadeInRight,
  FadeInUp,
  FadeOut,
  FadeOutDown,
  FadeOutLeft,
  FadeOutRight,
  FadeOutUp,
  FlipInEasyY,
  FlipOutEasyY,
  interpolate,
  interpolateColor,
  Keyframe,
  LinearTransition,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withSpring,
  withTiming,
  ZoomIn,
  ZoomOut,
}

type LayoutAnimationConfigOptions = PropsWithChildren<{
  skipEntering?: boolean
  skipExiting?: boolean
}>

export function LayoutAnimationConfig({ children }: LayoutAnimationConfigOptions) {
  return children
}

const Animated = {
  ...RNAnimated,
  View: createAnimatedComponent(View),
  Text: createAnimatedComponent(Text),
  Image: createAnimatedComponent(Image),
  ScrollView: createAnimatedComponent(ScrollView),
  FlatList: createAnimatedComponent(FlatList),
  createAnimatedComponent,
}

// biome-ignore lint/style/noDefaultExport: shim must mirror react-native-reanimated default export.
export default Animated
